import {
  Descriptions,
  Button,
  Icons,
  Table,
  TextField,
  Space,
  EditButton,
  useTable,
  useModalForm,
} from '@pankod/refine-antd'
import React from 'react'
import { useCan, useDelete } from '@pankod/refine-core'

import { PopDeleteConfirm } from '@resources/lms-management/components/PopDeleteConfirm'
import ShowClassButton from './ShowKelasButton'
import { TLMSClass, TLMSClassRequest } from '../types'
import { TCommonError } from 'src/interfaces/common'
import { ModalForm } from './ModalForm'
import { PROGRAM_LOCAL_STATE } from '@resources/lms-management/program-lokal/types'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'

type TClassTableListProps = {
  programId?: number
  programLocalId?: number
  hideTitle?: boolean
  programLocalStatus?: PROGRAM_LOCAL_STATE
}
export const TableClassList = ({
  hideTitle = false,
  programId,
  programLocalId,
  programLocalStatus,
}: TClassTableListProps) => {
  const CLASS_RESOURCE = `programs/${programId}/local-programs/${programLocalId}/classes`
  const { tableProps } = useTable<TLMSClass, TCommonError>({
    resource: CLASS_RESOURCE,
    dataProviderName: 'lms',
    queryOptions: {
      enabled: !!programId,
    },
  })

  const { mutateAsync } = useDelete()

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createShow,
    close: createClose,
  } = useModalForm<TLMSClass, TCommonError, TLMSClassRequest>({
    action: 'create',
    resource: CLASS_RESOURCE,
    dataProviderName: 'lms',
    autoResetForm: true,
    redirect: false,
    successNotification: {
      message: 'Kelas berhasil dibuat',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (err) =>
      showErrorNotification(err, 'Terdapat gangguan saat membuat kelas'),
  })

  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editShow,
    close: editClose,
  } = useModalForm<TLMSClass, TCommonError, TLMSClassRequest>({
    action: 'edit',
    resource: CLASS_RESOURCE,
    dataProviderName: 'lms',
    autoResetForm: true,
    redirect: false,
    successNotification: {
      message: 'Kelas berhasil diubah',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (err) =>
      showErrorNotification(err, 'Terdapat gangguan saat mengubah kelas'),
  })

  const { data: canEdit } = useCan({
    action: 'edit',
    resource: 'lms-class-management',
  })

  const { data: canDelete } = useCan({
    action: 'delete',
    resource: 'lms-class-management',
  })

  const { data: canCreate } = useCan({
    action: 'create',
    resource: 'lms-class-management',
  })

  const disableEditAction =
    !canEdit?.can || programLocalStatus !== PROGRAM_LOCAL_STATE.DRAFT

  const disableDeleteAction =
    !canDelete?.can || programLocalStatus !== PROGRAM_LOCAL_STATE.DRAFT

  const disableCreateAction =
    !canCreate?.can || programLocalStatus !== PROGRAM_LOCAL_STATE.DRAFT

  return (
    <>
      <Descriptions
        title={hideTitle ? null : 'Kelas'}
        extra={
          <Button
            disabled={disableCreateAction}
            onClick={() => createShow()}
            icon={<Icons.PlusOutlined />}
          >
            Tambah Kelas
          </Button>
        }
      >
        <Descriptions.Item span={1}>
          <Table
            {...tableProps}
            rowKey="id"
            className="w-full"
            bordered
            size="small"
          >
            <Table.Column
              dataIndex="id"
              title="ID"
              render={(value) => <TextField value={value} />}
            />
            <Table.Column
              dataIndex="name"
              title="Nama"
              render={(value) => <TextField value={value} />}
            />
            <Table.Column
              dataIndex="totalParticipants"
              title="Jumlah Partisipan"
              render={(value) => (
                <TextField
                  value={value || 'Kosong'}
                  italic={!value}
                  type={!value ? 'secondary' : undefined}
                />
              )}
              width={100}
            />
            <Table.Column<TLMSClass>
              render={(_, record) => (
                <Space>
                  <ShowClassButton
                    block
                    size="small"
                    recordItemId={record.id}
                    programId={programId}
                    programLocalId={programLocalId}
                  />
                  <EditButton
                    size="small"
                    recordItemId={record.id}
                    disabled={disableEditAction}
                    onClick={() => {
                      editShow(record.id)
                    }}
                  />
                  <PopDeleteConfirm
                    title={`Apakah Anda yakin ingin menghapus Kelas "${record.name}"?`}
                    placeholder="Tulis ulang nama Kelas"
                    onConfirm={() =>
                      mutateAsync({
                        id: record.id,
                        resource: CLASS_RESOURCE,
                        dataProviderName: 'lms',
                        successNotification: () => {
                          return {
                            message: 'Kelas berhasil dihapus',
                            type: 'success',
                            description: 'Sukses',
                          }
                        },
                        errorNotification: (err) =>
                          showErrorNotification(
                            err,
                            'Terdapat gangguan saat menghapus kelas',
                          ),
                      })
                    }
                    okButtonProps={{
                      danger: true,
                      type: 'default',
                    }}
                    okText="Delete"
                    validateValue={record.name}
                    errorMessage="Nama kelas tidak sama"
                    disabled={disableDeleteAction}
                  >
                    <Button
                      size="small"
                      danger
                      block
                      disabled={disableDeleteAction}
                      icon={<Icons.DeleteOutlined />}
                    >
                      Hapus
                    </Button>
                  </PopDeleteConfirm>
                </Space>
              )}
            />
          </Table>
        </Descriptions.Item>
      </Descriptions>

      <ModalForm
        formProps={createFormProps}
        modalProps={createModalProps}
        mode="create"
        onClose={() => {
          createFormProps.form?.resetFields()
          createClose()
        }}
      />

      <ModalForm
        formProps={editFormProps}
        modalProps={editModalProps}
        mode="edit"
        onClose={() => {
          editFormProps.form?.resetFields()
          editClose()
        }}
      />
    </>
  )
}
